import { Link } from "react-router-dom";

import { Pill } from "@/components/kit";
import type { EnvelopeView, FieldDef, LeaderboardRow, ParticipantRef } from "@/lib/api/types";
import { displayName } from "@/lib/format";
import { SportGlyph } from "@/lib/sportIcons";

import styles from "./FixtureDetail.module.css";

const WHEN = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
});

/** "finishTime" → "Finish time", for anything the definition does not label. */
function humanise(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function labelFor(fields: FieldDef[], key: string): string {
  return fields.find((f) => f.key === key)?.label ?? humanise(key);
}

/** Values are rendered by what the definition says they are, not by guesswork. */
function formatValue(field: FieldDef | undefined, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (field?.type === "enum") {
    const option = field.options?.find((o) => String(o.value) === String(value));
    return option?.label ?? String(value);
  }
  if (field?.type === "duration" && typeof value === "number") {
    const seconds = field.unit === "ms" ? value / 1000 : value;
    const minutes = Math.floor(seconds / 60);
    const rest = (seconds % 60).toFixed(2).padStart(5, "0");
    return minutes > 0 ? `${minutes}:${rest}` : `${rest}s`;
  }
  if (field?.type === "ratio" && typeof value === "number") {
    return field.asPercent ? `${Math.round(value * 100)}%` : value.toFixed(2);
  }
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function Facts({ blob, fields }: { blob: Record<string, unknown>; fields: FieldDef[] }) {
  const entries = Object.entries(blob);
  if (entries.length === 0) return <p className={styles.none}>Nothing recorded.</p>;
  return (
    <dl className={styles.facts}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.fact}>
          <dt className={styles.factKey}>{labelFor(fields, key)}</dt>
          <dd className={styles.factValue}>
            {formatValue(
              fields.find((f) => f.key === key),
              value,
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Lineup({ people }: { people: ParticipantRef[] }) {
  return (
    <ol className={styles.lineup}>
      {people.map((person, i) => (
        <li key={person.code ?? i} className={styles.lineupItem}>
          <span className={styles.lineupNumber}>{i + 1}</span>
          <span className={styles.lineupName}>{person.name}</span>
          {person.roles?.map((role) => (
            <span key={role} className={styles.roleTag}>
              {role}
            </span>
          ))}
        </li>
      ))}
    </ol>
  );
}

/** Two sides, the score between them, and who each of them fielded. */
function Scoreline({ env }: { env: EnvelopeView }) {
  const sides = env.participants ?? [];
  const result = env.result as
    | { outcome?: string; winner?: string; method?: string; score?: Record<string, number> }
    | undefined;
  const alignment = (env.alignment ?? {}) as Record<string, string>;
  const settled = env.status === "COMPLETED";

  return (
    <>
      <section className={styles.scoreCard}>
        {sides.map((side, i) => {
          const won = settled && result?.winner === side.code;
          const score = result?.score?.[side.code ?? ""];
          return (
            <div key={side.code ?? i} className={`${styles.side} ${won ? styles.sideWon : ""}`}>
              <div className={styles.sideHead}>
                {alignment[side.code ?? ""] && (
                  <span className={styles.alignment}>{alignment[side.code ?? ""]}</span>
                )}
                {/* A slot whose party is not decided yet — its name is a label. */}
                {side.placeholder && <span className={styles.alignment}>TBD</span>}
                {won && <span className={styles.wonTag}>Winner</span>}
              </div>
              <div className={styles.sideName}>{side.name}</div>
              <div className={styles.sideScore}>{score ?? (settled ? "—" : "")}</div>
            </div>
          );
        })}
      </section>

      {settled && result?.outcome && (
        <p className={styles.outcome}>
          {result.outcome === "DRAW"
            ? "Drawn."
            : `${sides.find((s) => s.code === result.winner)?.name ?? "Winner"} won${
                result.method ? ` by ${humanise(result.method).toLowerCase()}` : ""
              }.`}
        </p>
      )}

      {sides.some((s) => (s.lineup ?? []).length > 0) && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Lineups</h2>
          <div className={styles.lineups}>
            {sides.map((side, i) => (
              <div key={side.code ?? i}>
                <h3 className={styles.lineupTitle}>{side.name}</h3>
                {(side.lineup ?? []).length > 0 ? (
                  <Lineup people={side.lineup ?? []} />
                ) : (
                  <p className={styles.none}>No lineup recorded.</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/** A ranked field: the order they finished, and who rode with each of them. */
function Leaderboard({ rows, metricFields }: { rows: LeaderboardRow[]; metricFields: FieldDef[] }) {
  const used = metricFields.filter((f) => rows.some((r) => r.metric?.[f.key] !== undefined));
  return (
    <table>
      <thead>
        <tr>
          <th className={styles.rankColumn}>#</th>
          <th>Runner</th>
          {used.map((field) => (
            <th key={field.key}>{field.label ?? humanise(field.key)}</th>
          ))}
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.participant.code ?? i}>
            <td className={styles.rankColumn}>
              <span className={row.rank === 1 ? styles.rankFirst : styles.rank}>
                {row.rank ?? "—"}
              </span>
            </td>
            <td>
              <div className={styles.runnerName}>
                {row.participant.name}
                {row.participant.placeholder && " (TBD)"}
              </div>
              {row.participant.connections && (
                <div className={styles.connections}>
                  {Object.entries(row.participant.connections).map(([role, who]) => (
                    <span key={role} className={styles.connection}>
                      <span className={styles.connectionRole}>{role}</span>
                      {who}
                    </span>
                  ))}
                </div>
              )}
            </td>
            {used.map((field) => (
              <td key={field.key} >
                {formatValue(field, row.metric?.[field.key])}
              </td>
            ))}
            <td>
              <Pill>{row.resultStatus}</Pill>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function FixtureDetail({
  env,
  formatFields,
  metricFields,
  icon,
  fixtureId,
}: {
  env: EnvelopeView;
  formatFields: FieldDef[];
  metricFields: FieldDef[];
  /** The sport's own emoji, from its definition. */
  icon?: string | null;
  /** The row id, for the scoring console link. */
  fixtureId?: string;
}) {
  const sport = (env.sportCode as string | undefined) ?? "";
  const competition = env.competition as string | undefined;
  const venue = env.fixture.venue as
    | { name?: string; city?: string; country?: string }
    | undefined;
  const start = env.fixture.scheduledStart as string | undefined;
  const officials = (env.officials ?? []) as { role: string; participant: ParticipantRef }[];
  const timeline = (env.timeline ?? []) as { seq: number; unit: string; label?: string }[];

  return (
    <>
      <Link className={styles.back} to={sport ? `/${sport}/fixtures` : "/fixtures"}>
        ← {sport ? `${displayName(sport)} fixtures` : "Fixtures"}
      </Link>

      <header className={styles.head}>
        <div className={styles.headMain}>
          <span className={styles.sportBadge}>
            <SportGlyph id={icon} size={20} />
          </span>
          <div className={styles.headText}>
            <h1 className={styles.title}>{env.fixture.name ?? env.fixture.code ?? "Fixture"}</h1>
            <p className={styles.subtitle}>
              <code className={styles.code}>{env.fixture.code}</code>
              {competition && <> · {competition}</>}
              {sport && <> · {displayName(sport)}</>}
            </p>
          </div>
        </div>
        <div className={styles.badges}>
          <Pill tone={env.status === "LIVE" ? "live" : "neutral"} dot={env.status === "LIVE"}>
            {env.status}
          </Pill>
          {env.resultsOfficial === true && <Pill tone="good">Official</Pill>}
          {fixtureId && (
            // A finished match is still opened in an app: Results corrects it
            // through the operations console.
            <Link className={styles.scoreLink} to={`/scoring-console/${fixtureId}`}>
              {env.status === "COMPLETED" ? "Open in an app" : "Score this match"}
            </Link>
          )}
          {fixtureId && sport && (
            <Link className={styles.scoreLink} to={`/${sport}/fixtures/${fixtureId}/edit`}>
              {/* A live or finished fixture is edited as an append-only
                  correction; a scheduled one is amended in place. */}
              {env.status === "SCHEDULED" || env.status === "POSTPONED" ? "Edit" : "Correct"}
            </Link>
          )}
          {fixtureId && sport && (
            <Link className={styles.scoreLink} to={`/${sport}/stats/fixture/${fixtureId}`}>
              Numbers
            </Link>
          )}
        </div>
      </header>

      <section className={styles.meta}>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>When</span>
          <span className={styles.metaValue}>{start ? WHEN.format(new Date(start)) : "—"}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>Where</span>
          <span className={styles.metaValue}>
            {venue?.name ?? "—"}
            {venue?.city && <span className={styles.metaSub}> {venue.city}</span>}
          </span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaKey}>Officials</span>
          <span className={styles.metaValue}>
            {officials.length === 0
              ? "—"
              : officials.map((o) => `${o.participant.name} (${o.role})`).join(", ")}
          </span>
        </div>
      </section>

      {env.kind === "H2H" ? (
        <Scoreline env={env} />
      ) : (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Result</h2>
          {env.leaderboard && env.leaderboard.length > 0 ? (
            <Leaderboard rows={env.leaderboard} metricFields={metricFields} />
          ) : (
            <p className={styles.none}>No entrants recorded.</p>
          )}
        </section>
      )}

      <div className={styles.split}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>How it was played</h2>
          <Facts blob={env.fixture.format ?? {}} fields={formatFields} />
        </section>

        {timeline.length > 0 && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Timeline</h2>
            <ol className={styles.timeline}>
              {timeline.map((item) => (
                <li key={`${item.unit}-${item.seq}`} className={styles.timelineItem}>
                  <span className={styles.timelineSeq}>{item.seq}</span>
                  {item.label ?? `${humanise(item.unit)} ${item.seq}`}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </>
  );
}
